import React from 'react'
import {
  GraduationCap,
  Plus,
  Users,
  Layers,
  Sparkles,
  LayoutGrid,
  CheckCircle2,
  AlertTriangle,
  Radio,
  UserCheck
} from 'lucide-react'
import { GrupoInstitucion, ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  grupos: GrupoInstitucion[]
  salonActivoId: number | 'TODOS'
  onSeleccionarSalon: (salonId: number | 'TODOS') => void
  expedientes: ExpedienteDespacho[]
  totalAlumnos: number
  onAbrirCrearSalon: () => void
  onAbrirConfiguradorConSalon: (salonId?: number) => void
  onAbrirVerAlumnosSalon?: (salon: GrupoInstitucion) => void
  vistaAgrupada: boolean
  onToggleVistaAgrupada: () => void
}

export const BarraCoordinadorSalones: React.FC<Props> = ({
  grupos,
  salonActivoId,
  onSeleccionarSalon,
  expedientes,
  totalAlumnos,
  onAbrirCrearSalon,
  onAbrirConfiguradorConSalon,
  onAbrirVerAlumnosSalon,
  vistaAgrupada,
  onToggleVistaAgrupada
}) => {
  // Salón activo seleccionado
  const salonActivo = grupos.find((g) => g.id_grupo === salonActivoId)

  // Expedientes del salón activo
  const expedientesSalonActivo = React.useMemo(() => {
    if (salonActivoId === 'TODOS') return expedientes
    return expedientes.filter(
      (e) => e.idGrupo === salonActivoId || (salonActivo && e.grupoNombre === salonActivo.nombre)
    )
  }, [expedientes, salonActivoId, salonActivo])

  // Métricas del salón activo
  const metricasSalon = React.useMemo(() => {
    return {
      total: expedientesSalonActivo.length,
      esperandoModulacion: expedientesSalonActivo.filter((e) => e.estado === 'ESPERANDO_MODULACION').length,
      revisionReceptor: expedientesSalonActivo.filter((e) => e.estado === 'EN_REVISION_RECEPTOR').length,
      desaduanados: expedientesSalonActivo.filter((e) => e.estado === 'DESADUANADO' || e.estado === 'CERRADO').length,
      incidencias: expedientesSalonActivo.filter((e) => e.estado === 'INCIDENCIA').length
    }
  }, [expedientesSalonActivo])

  return (
    <div className="space-y-4">
      {/* ========================================================================= */}
      {/* 1. SELECTOR PRINCIPAL DE SALONES (PILLS SEGMENTADAS GLASSMÓRFICAS) */}
      {/* ========================================================================= */}
      <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-3 sm:p-4 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Selector de Píldoras de Salones */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 no-scrollbar touch-pan-x bg-slate-100/70 dark:bg-white/[0.03] p-1.5 rounded-2xl border border-slate-200/50 dark:border-white/[0.05]">
          <div className="flex items-center gap-1.5 px-2 text-xs font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 shrink-0">
            <GraduationCap className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            <span className="hidden sm:inline">Salón:</span>
          </div>

          {/* Opción 1: Todos los Salones */}
          <button
            type="button"
            onClick={() => onSeleccionarSalon('TODOS')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 ${
              salonActivoId === 'TODOS'
                ? 'bg-white dark:bg-white/10 text-cyan-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'
            }`}
          >
            <span>Todos los Salones</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                salonActivoId === 'TODOS'
                  ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300'
                  : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-zinc-400'
              }`}
            >
              {expedientes.length}
            </span>
          </button>

          {/* Opciones Dinámicas por Cada Salón Registrado */}
          {grupos.map((grupo) => {
            const esActivo = salonActivoId === grupo.id_grupo
            const expCount = expedientes.filter(
              (e) => e.idGrupo === grupo.id_grupo || e.grupoNombre === grupo.nombre
            ).length

            return (
              <button
                key={grupo.id_grupo}
                type="button"
                onClick={() => onSeleccionarSalon(grupo.id_grupo)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 ${
                  esActivo
                    ? 'bg-white dark:bg-white/10 text-indigo-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
                    : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'
                }`}
              >
                <span>{grupo.nombre}</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                    esActivo
                      ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300'
                      : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                  title={`${expCount} expedientes`}
                >
                  {expCount}
                </span>
                {grupo.miembros_count !== undefined && grupo.miembros_count > 0 && (
                  <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">
                    ({grupo.miembros_count} alum)
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Acciones de la derecha: Crear Salón & Alternar Vista */}
        <div className="flex items-center gap-2 shrink-0">
          {salonActivoId === 'TODOS' && (
            <button
              type="button"
              onClick={onToggleVistaAgrupada}
              className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border ${
                vistaAgrupada
                  ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200/60 dark:border-white/10 text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/5'
              }`}
              title="Alternar entre agrupar por salón o lista continua"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">
                {vistaAgrupada ? 'Agrupado por Salón' : 'Lista Unificada'}
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={onAbrirCrearSalon}
            className="group relative flex items-center gap-1.5 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-zinc-300 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white dark:hover:bg-white/[0.05]"
            title="Crear un nuevo grupo/salón institucional"
          >
            <Plus className="w-3.5 h-3.5 text-cyan-500 transition-transform group-hover:rotate-90" />
            <span>+ Salón</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. CABECERA CONTEXTUAL DEL SALÓN ACTIVO (ESTILO BENTO PREMIUM) */}
      {/* ========================================================================= */}
      {salonActivo ? (
        <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-200/60 dark:border-white/[0.06]">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  Salón Activo
                </span>
                {salonActivo.descripcion && (
                  <span className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
                    {salonActivo.descripcion}
                  </span>
                )}
                <span className="text-xs text-slate-300 dark:text-zinc-700">·</span>
                <span className="text-xs text-slate-500 dark:text-zinc-400 flex items-center gap-1 font-medium">
                  <Users className="w-3.5 h-3.5 text-indigo-500" />
                  {salonActivo.miembros_count !== undefined && salonActivo.miembros_count > 0
                    ? `${salonActivo.miembros_count} Alumnos matriculados`
                    : `${totalAlumnos} Alumnos en campus`}
                </span>
              </div>

              <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5 mt-1">
                <span>{salonActivo.nombre}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-zinc-300">
                  {metricasSalon.total} expedientes
                </span>
              </h2>
            </div>

            {/* Acciones del Docente para este Salón */}
            <div className="flex flex-wrap items-center gap-2">
              {onAbrirVerAlumnosSalon && (
                <button
                  type="button"
                  onClick={() => onAbrirVerAlumnosSalon(salonActivo)}
                  className="px-3.5 py-2 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] text-slate-700 dark:text-zinc-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5 shadow-sm"
                >
                  <Users className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Ver Alumnos</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => onAbrirConfiguradorConSalon(salonActivo.id_grupo)}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 text-white dark:text-black font-extrabold text-xs shadow-md shadow-blue-500/20 dark:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02] flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>+ Generar Práctica con Células</span>
              </button>
            </div>
          </div>

          {/* Mini-Chips de Estado del Salón Activo */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4 text-xs">
            <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5">
              <Radio className="w-4 h-4 text-amber-500 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 block font-bold uppercase">Modulación</span>
                <span className="font-extrabold text-slate-900 dark:text-white">
                  {metricasSalon.esperandoModulacion} en espera
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5">
              <UserCheck className="w-4 h-4 text-purple-500 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 block font-bold uppercase">Receptor</span>
                <span className="font-extrabold text-slate-900 dark:text-white">
                  {metricasSalon.revisionReceptor} cotejando
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 block font-bold uppercase">Desaduanados</span>
                <span className="font-extrabold text-slate-900 dark:text-white">
                  {metricasSalon.desaduanados} concluidos
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5">
              <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 block font-bold uppercase">Incidencias</span>
                <span className="font-extrabold text-rose-500">
                  {metricasSalon.incidencias} discrepancias
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Vista Global de Todos los Salones */
        <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-4 sm:p-5 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-500 shrink-0">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <p className="font-extrabold text-slate-900 dark:text-white">
                Vista Global: {grupos.length} Salón{grupos.length !== 1 ? 'es' : ''} Institucional{grupos.length !== 1 ? 'es' : ''}
              </p>
              <p className="text-slate-500 dark:text-zinc-400 text-xs">
                Monitoreando todas las operaciones simultáneas. Selecciona una pestaña arriba para enfocar una sola clase.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onAbrirConfiguradorConSalon()}
            className="px-4 py-2 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 font-bold text-xs border border-indigo-500/25 transition-all flex items-center justify-center gap-1.5 shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Configurar Prácticas por Aulas</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default BarraCoordinadorSalones
